import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import { useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Download,
  FileSearch,
  Home,
  LoaderCircle,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { supabase } from "../supabaseClient";
import { sourceFingerprint } from "../utils/atsAnalysis";

const GENERATED_ANALYSIS_KEY = "optimizedResumeAnalysisId";

const GENERATION_STAGES = [
  "Preparing your resume structure",
  "Aligning experience with the role",
  "Improving bullet points",
  "Formatting the selected template",
  "Finalising your document",
];

const PAGE = {
  w: 210,
  h: 297,
  margin: { top: 18, right: 16, bottom: 16, left: 16 },
};

const SECTION_TITLES = [
  "PROFESSIONAL SUMMARY",
  "SUMMARY",
  "SKILLS",
  "TECHNICAL SKILLS",
  "CORE SKILLS",
  "EXPERIENCE",
  "WORK EXPERIENCE",
  "PROFESSIONAL EXPERIENCE",
  "PROJECTS",
  "EDUCATION",
  "CERTIFICATIONS",
  "ACHIEVEMENTS",
  "PUBLICATIONS",
  "LANGUAGES",
  "INTERESTS",
];

const TEMPLATE_CONFIG = {
  modern: {
    label: "Modern Professional",
    bodySize: 10.8,
    headingSize: 12.5,
    nameSize: 18.5,
    metaSize: 9.8,
    lineGap: 5.2,
    paraGap: 2.0,
    sectionTopGap: 2.2,
    sectionBottomGap: 1.2,
    bulletIndent: 5,
    headerCenter: false,
    sectionUppercase: true,
  },
  classic: {
    label: "Classic Corporate",
    bodySize: 10.6,
    headingSize: 12.0,
    nameSize: 17.5,
    metaSize: 9.5,
    lineGap: 5.0,
    paraGap: 1.8,
    sectionTopGap: 2.0,
    sectionBottomGap: 1.0,
    bulletIndent: 5,
    headerCenter: false,
    sectionUppercase: true,
  },
  minimal: {
    label: "Minimal ATS",
    bodySize: 10.9,
    headingSize: 11.8,
    nameSize: 17.2,
    metaSize: 9.5,
    lineGap: 5.0,
    paraGap: 1.8,
    sectionTopGap: 1.8,
    sectionBottomGap: 0.8,
    bulletIndent: 5,
    headerCenter: false,
    sectionUppercase: true,
  },
};

const GenerateResume = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  const {
    name = "",
    missingKeywords = [],
    originalResume = "",
    jobDescription: stateJobDescription = "",
    optimizationHandoff = {},
    analysisContext = "",
    comparisonContext = null,
  } = location.state || {};

  const jobDescription = stateJobDescription || localStorage.getItem("jobDescription") || "";
  const safeMissingKeywords = Array.isArray(missingKeywords) ? missingKeywords : [];
  const hasValidAnalysis = Boolean(location.state && originalResume.trim());
  const analysisId = hasValidAnalysis
    ? sourceFingerprint(originalResume, jobDescription, safeMissingKeywords, optimizationHandoff)
    : "";

  const [optimizedResume, setOptimizedResume] = useState(() => {
    const storedAnalysisId = localStorage.getItem(GENERATED_ANALYSIS_KEY) || "";
    return hasValidAnalysis && storedAnalysisId === analysisId
      ? localStorage.getItem("optimizedResume") || ""
      : "";
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [limitReached, setLimitReached] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("modern");
  const [hasAttemptedGeneration, setHasAttemptedGeneration] = useState(() => Boolean(
    hasValidAnalysis &&
    localStorage.getItem(GENERATED_ANALYSIS_KEY) === analysisId &&
    localStorage.getItem("optimizedResume")
  ));
  const [generationStage, setGenerationStage] = useState(0);

  useEffect(() => {
    if (!hasValidAnalysis) return;
    const storedAnalysisId = localStorage.getItem(GENERATED_ANALYSIS_KEY) || "";
    if (storedAnalysisId !== analysisId) {
      setOptimizedResume("");
      setHasAttemptedGeneration(false);
      setError("");
      setLimitReached(false);
      setGenerationStage(0);
      localStorage.removeItem("optimizedResume");
      localStorage.removeItem(GENERATED_ANALYSIS_KEY);
    }
  }, [analysisId, hasValidAnalysis]);

  useEffect(() => {
    if (!loading || reduceMotion) return undefined;
    const interval = window.setInterval(() => {
      setGenerationStage((current) => Math.min(current + 1, GENERATION_STAGES.length - 1));
    }, 1600);
    return () => window.clearInterval(interval);
  }, [loading, reduceMotion]);

  const templateOptions = useMemo(
    () => [
      { key: "modern", label: "Modern Professional", description: "Clean hierarchy with a polished professional finish." },
      { key: "classic", label: "Classic Corporate", description: "Traditional structure for formal and corporate roles." },
      { key: "minimal", label: "Minimal ATS", description: "Simple, focused formatting for straightforward scanning." },
    ],
    []
  );

  const generateOptimizedResume = async () => {
    if (loading) return;

    setLoading(true);
    setError("");
    setLimitReached(false);
    setHasAttemptedGeneration(true);
    setGenerationStage(0);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setError("You must be logged in to generate a resume.");
        return;
      }

      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "https://jobzam-backend.onrender.com";

      const response = await fetch(`${backendUrl}/generate_resume`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          original_resume: originalResume,
          missing_keywords: safeMissingKeywords,
          optimization_handoff: optimizationHandoff,
          analysis_context: analysisContext,
          comparison_context: comparisonContext,
          email: user.email,
        }),
      });

      if (response.status === 403) {
        setLimitReached(true);
        setOptimizedResume("");
        localStorage.removeItem("optimizedResume");
        localStorage.removeItem(GENERATED_ANALYSIS_KEY);
        return;
      }

      let responseData = {};
      try {
        responseData = await response.json();
      } catch {
        responseData = {};
      }

      if (!response.ok) {
        const errorText =
          responseData.detail ||
          "Resume optimization is temporarily unavailable. Please try again.";
        setError(errorText);
        setOptimizedResume("");
        localStorage.removeItem("optimizedResume");
        localStorage.removeItem(GENERATED_ANALYSIS_KEY);
        return;
      }

      const rawOptimized = responseData.optimized_resume || "";

      if (!rawOptimized.trim()) {
        setError("Resume optimization returned empty content. Please try again.");
        setOptimizedResume("");
        localStorage.removeItem("optimizedResume");
        localStorage.removeItem(GENERATED_ANALYSIS_KEY);
        return;
      }

      setOptimizedResume(rawOptimized);
      localStorage.setItem("optimizedResume", rawOptimized);
      localStorage.setItem(GENERATED_ANALYSIS_KEY, analysisId);
    } catch (err) {
      console.error("Error generating optimized resume:", err);
      setError("Resume optimization is temporarily unavailable. Please try again.");
      setOptimizedResume("");
      localStorage.removeItem("optimizedResume");
      localStorage.removeItem(GENERATED_ANALYSIS_KEY);
    } finally {
      setLoading(false);
    }
  };

  const goCoverLetter = () => {
    const jd = jobDescription;
    if (optimizedResume) {
      localStorage.setItem("optimizedResume", optimizedResume);
    }
    navigate("/cover-letter", { state: { optimizedResume, jobDescription: jd } });
  };

  function normalizeResumeText(text) {
    if (!text) return "";

    let t = String(text).replace(/\r/g, "").trim();

    const fenced = t.match(/```(?:[\w-]+)?\n?([\s\S]*?)```/);
    if (fenced) {
      t = fenced[1].trim();
    }

    t = t
      .replace(/^\s*```+\s*$/gm, "")
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/^#+\s*/gm, "")
      .replace(/^\s*[-*]\s+/gm, "• ")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

    SECTION_TITLES.forEach((heading) => {
      const rx = new RegExp(`\\b${heading}\\b:?`, "gi");
      t = t.replace(rx, heading.toUpperCase());
    });

    return t;
  }

  function isSection(line) {
    const trimmed = line.trim().replace(/:$/, "");
    return SECTION_TITLES.some((title) => title.toLowerCase() === trimmed.toLowerCase());
  }

  function isBullet(line) {
    return /^\s*•\s+/.test(line);
  }

  function looksLikeEmail(line) {
    return /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(line);
  }

  function looksLikePhone(line) {
    return /(\+?\d[\d\s().-]{7,}\d)/.test(line);
  }

  function looksLikeUrlish(line) {
    return /\b(linkedin|github|portfolio|www\.|http)\b/i.test(line);
  }

  function looksLikeLocation(line) {
    return /\b(london|uk|united kingdom|england|manchester|birmingham|leeds|glasgow)\b/i.test(line);
  }

  function isGarbageHeaderLine(line) {
    const trimmed = line.trim();
    if (!trimmed) return true;
    if (/^`+$/.test(trimmed)) return true;
    if (/^[`'".,\\/\-_|~]+$/.test(trimmed)) return true;
    return false;
  }

  function splitResumeIntoLines(text) {
    return normalizeResumeText(text)
      .split("\n")
      .map((line) => line.trimEnd());
  }

  function extractHeaderFromResume(text) {
    const allLines = splitResumeIntoLines(text);
    const lines = allLines.filter((line) => line.trim() !== "" && !isGarbageHeaderLine(line));

    let headerName = "";
    let headerMetaLines = [];
    let bodyStartIndex = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (isSection(line)) {
        bodyStartIndex = i;
        break;
      }

      if (!headerName) {
        const lineLooksLikeName =
          !looksLikeEmail(line) &&
          !looksLikePhone(line) &&
          !looksLikeUrlish(line) &&
          !isBullet(line) &&
          line.length > 2 &&
          line.length <= 60;

        if (lineLooksLikeName) {
          headerName = line;
          continue;
        }
      }

      const looksLikeMeta =
        looksLikeEmail(line) ||
        looksLikePhone(line) ||
        looksLikeUrlish(line) ||
        looksLikeLocation(line) ||
        /\|/.test(line);

      if (looksLikeMeta) {
        headerMetaLines.push(line);
        continue;
      }

      bodyStartIndex = i;
      break;
    }

    if (!headerName) {
      bodyStartIndex = 0;
    }

    const bodyLines = lines.slice(bodyStartIndex).filter((line) => !isGarbageHeaderLine(line));

    return {
      headerName,
      headerMeta: headerMetaLines.join(" | ").replace(/\s+\|\s+\|/g, " | "),
      bodyLines,
    };
  }

  const previewResume = useMemo(() => normalizeResumeText(optimizedResume), [optimizedResume]);

  const downloadResume = () => {
    if (!optimizedResume) return;

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const cfg = TEMPLATE_CONFIG[selectedTemplate] || TEMPLATE_CONFIG.modern;

    const usableWidth = PAGE.w - PAGE.margin.left - PAGE.margin.right;
    const usableHeight = PAGE.h - PAGE.margin.top - PAGE.margin.bottom;

    let y = PAGE.margin.top;

    const ensureSpace = (requiredHeight = cfg.lineGap) => {
      if (y + requiredHeight > PAGE.margin.top + usableHeight) {
        doc.addPage();
        y = PAGE.margin.top;
      }
    };

    const writeWrapped = ({
      text,
      x = PAGE.margin.left,
      bold = false,
      fontSize = cfg.bodySize,
      align = "left",
      extraGap = 0,
      maxWidth = usableWidth,
    }) => {
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(fontSize);

      const lines = doc.splitTextToSize(text, maxWidth);

      lines.forEach((line) => {
        ensureSpace(cfg.lineGap);
        doc.text(line, x, y, { align });
        y += cfg.lineGap;
      });

      if (extraGap) y += extraGap;
    };

    const writeSectionHeading = (text) => {
      ensureSpace(cfg.lineGap + cfg.sectionTopGap + cfg.sectionBottomGap + 1);

      y += cfg.sectionTopGap;

      const headingText = cfg.sectionUppercase
        ? text.replace(/:$/, "").toUpperCase()
        : text.replace(/:$/, "");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(cfg.headingSize);
      doc.text(headingText, PAGE.margin.left, y);

      y += cfg.lineGap - 0.8 + cfg.sectionBottomGap;
    };

    const writeBullet = (line) => {
      const bulletText = line.replace(/^\s*•\s+/, "").trim();
      const bulletX = PAGE.margin.left;
      const textX = PAGE.margin.left + cfg.bulletIndent;
      const wrapWidth = usableWidth - cfg.bulletIndent;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(cfg.bodySize);

      const wrapped = doc.splitTextToSize(bulletText, wrapWidth);

      wrapped.forEach((chunk, index) => {
        ensureSpace(cfg.lineGap);
        if (index === 0) {
          doc.text("•", bulletX, y);
        }
        doc.text(chunk, textX, y);
        y += cfg.lineGap;
      });
    };

    const { headerName, headerMeta, bodyLines } = extractHeaderFromResume(optimizedResume);

    if (headerName) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(cfg.nameSize);
      doc.text(headerName, PAGE.margin.left, y);
      y += 8;
    }

    if (headerMeta) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(cfg.metaSize);

      const metaLines = doc.splitTextToSize(headerMeta, usableWidth);
      metaLines.forEach((line) => {
        ensureSpace(cfg.lineGap);
        doc.text(line, PAGE.margin.left, y);
        y += cfg.lineGap;
      });

      y += 1.5;
    }

    let previousLineWasBullet = false;

    bodyLines.forEach((raw, index) => {
      const line = raw.trim();

      if (!line) {
        y += cfg.paraGap;
        previousLineWasBullet = false;
        return;
      }

      const isJobTitleLine =
        !isBullet(line) &&
        !isSection(line) &&
        /(\(|\d{4}|present|present\)|-)/i.test(line) &&
        line.length < 120;

      if (isJobTitleLine && index !== 0) {
        y += 3.5;
      }

      if (isSection(line)) {
        writeSectionHeading(line);
        previousLineWasBullet = false;
        return;
      }

      if (isBullet(line)) {
        writeBullet(line);
        previousLineWasBullet = true;
        return;
      }

      if (previousLineWasBullet) {
        y += 1.5;
      }

      writeWrapped({
        text: line,
        fontSize: cfg.bodySize,
        bold: false,
      });

      previousLineWasBullet = false;
    });

    const safeFileName = `${(headerName || name || "Optimized_Resume")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "_")}_Resume.pdf`;

    doc.save(safeFileName);
  };

  if (!hasValidAnalysis) {
    return (
      <main className="relative min-h-[65vh] overflow-hidden bg-slate-50 px-4 py-10 sm:px-6">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-blue-50 to-transparent" />
        <section className="relative mx-auto max-w-xl rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-[0_24px_70px_-38px_rgba(37,99,235,0.3)] sm:p-8">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-700"><FileSearch aria-hidden="true" className="h-7 w-7" /></div>
          <h1 className="mt-5 text-2xl font-extrabold tracking-tight text-slate-950 sm:text-3xl">Start with an ATS analysis.</h1>
          <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-slate-600 sm:text-base">Upload your CV and add a job description before generating an optimised resume.</p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <button onClick={() => navigate("/upload")} className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700"><RefreshCw aria-hidden="true" className="h-4 w-4" /> Analyse My Resume</button>
            <button onClick={() => navigate("/")} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:text-blue-700"><Home aria-hidden="true" className="h-4 w-4" /> Return Home</button>
          </div>
        </section>
      </main>
    );
  }

  const workflowStep = optimizedResume ? 3 : loading ? 2 : 1;

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-50 px-3 py-7 min-[390px]:px-4 sm:px-6 sm:py-8 lg:px-8 lg:py-10 print-reset">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-blue-50 to-transparent" />
      <div className="relative mx-auto w-full max-w-6xl print-reset">
        <div className="mb-6 max-w-3xl print-reset">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-blue-700 shadow-sm"><Sparkles aria-hidden="true" className="h-3.5 w-3.5" /> Resume Builder</div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">Create your <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">optimised resume.</span></h1>
          <p className="mt-3 text-base leading-7 text-slate-600">Choose a professional template, generate a role-specific resume, and review it before downloading.</p>
        </div>

        <ol className="scrollbar-hide mb-6 flex max-w-full touch-pan-x overflow-x-auto overscroll-x-contain rounded-2xl border border-slate-200 bg-white p-2 shadow-sm" aria-label="Resume generation progress">
          {["Review match", "Choose template", "Generate", "Download"].map((step, index) => { const complete = index < workflowStep; const active = index === workflowStep; return <li key={step} className="flex min-w-[150px] flex-1 items-center last:min-w-[130px]"><div className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold ${active ? "bg-blue-50 text-blue-700" : complete ? "text-slate-800" : "text-slate-400"}`} aria-current={active ? "step" : undefined}><span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs ${active ? "bg-blue-600 text-white" : complete ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{complete ? <Check aria-hidden="true" className="h-4 w-4" /> : index + 1}</span><span className="whitespace-nowrap">{step}</span></div>{index < 3 && <div className={`mx-2 h-px flex-1 ${complete ? "bg-blue-300" : "bg-slate-200"}`} />}</li>; })}
        </ol>

        <div className="min-w-0 max-w-full rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_24px_70px_-42px_rgba(15,23,42,0.35)] min-[390px]:p-5 sm:p-7">

        <div className="mb-6">
          <div className="mb-5">
            <h2 className="text-xl font-bold text-slate-950">Choose your template</h2>
            <p className="mt-1 text-sm text-slate-600">The existing three ATS-friendly layouts use the same generated content.</p>

            <div className="mt-4 grid gap-3 md:grid-cols-3" role="radiogroup" aria-label="Resume template">
              {templateOptions.map((template) => (
                <button
                  key={template.key}
                  type="button"
                  role="radio"
                  aria-checked={selectedTemplate === template.key}
                  onClick={() => setSelectedTemplate(template.key)}
                  className={`rounded-2xl border p-4 text-left outline-none transition duration-200 focus:ring-4 focus:ring-blue-100 ${
                    selectedTemplate === template.key
                      ? "border-blue-500 bg-blue-50 shadow-sm"
                      : "border-slate-200 bg-white hover:border-blue-300"
                  }`}
                >
                  <span className="flex items-center justify-between gap-3"><span className="font-bold text-slate-900">{template.label}</span><span className={`flex h-5 w-5 items-center justify-center rounded-full border ${selectedTemplate === template.key ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300"}`}>{selectedTemplate === template.key && <Check aria-hidden="true" className="h-3 w-3" />}</span></span>
                  <span className="mt-1.5 block text-xs leading-5 text-slate-600">{template.description}</span>
                </button>
              ))}
            </div>
          </div>

          {!hasAttemptedGeneration ? (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-4">
                <p className="text-sm font-semibold text-blue-700">Selected template: {TEMPLATE_CONFIG[selectedTemplate].label}</p>
                <p className="text-xs text-gray-600 mt-1">
                  Choose your template, then generate your optimized resume when you’re ready.
                </p>
              </div>

              <div className="text-center">
                <button
                  onClick={generateOptimizedResume}
                  disabled={loading}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-6 sm:text-base"
                >
                  <Sparkles aria-hidden="true" className="h-4 w-4" /> Generate My Optimised Resume <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : loading ? (
            <div className="mx-auto min-w-0 max-w-xl py-4 text-center sm:py-6" aria-busy="true"><LoaderCircle aria-hidden="true" className={`mx-auto h-9 w-9 text-blue-600 ${reduceMotion ? "" : "animate-spin"}`} /><h3 className="mt-4 text-lg font-bold text-slate-950 sm:text-xl">Creating your optimised resume</h3><ol className="mt-4 space-y-2 text-left sm:mt-5">{GENERATION_STAGES.map((stage, index) => <li key={stage} className={`flex min-w-0 items-center gap-2.5 rounded-xl border px-3 py-2.5 text-sm font-medium sm:gap-3 sm:px-4 sm:py-3 ${index === generationStage ? "border-blue-200 bg-blue-50 text-blue-800" : index < generationStage ? "border-emerald-100 bg-emerald-50 text-slate-700" : "border-slate-100 text-slate-400"}`}>{index < generationStage ? <CheckCircle2 aria-hidden="true" className="h-4 w-4 shrink-0 text-emerald-600" /> : index === generationStage ? <LoaderCircle aria-hidden="true" className={`h-4 w-4 shrink-0 text-blue-600 ${reduceMotion ? "" : "animate-spin"}`} /> : <span className="h-4 w-4 shrink-0 rounded-full border-2 border-slate-200" />}<span className="min-w-0 break-words">{stage}</span></li>)}</ol></div>
          ) : limitReached ? (
            <div className="flex min-w-0 flex-col items-center break-words rounded-lg border border-yellow-400 bg-yellow-50 p-4 text-center">
              <span className="text-red-600 font-semibold mb-2">
                🚫 You’ve used all 3 free resume generations.
              </span>
              <button
                onClick={() => navigate("/pricing")}
                className="mt-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-2 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 font-bold"
              >
                Upgrade to Premium for Unlimited Resumes
              </button>
              <a
                href="/pricing"
                className="mt-2 text-blue-600 underline font-semibold"
                target="_blank"
                rel="noopener noreferrer"
              >
                Or click here to view premium plans
              </a>
            </div>
          ) : error ? (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="break-words text-red-600 font-medium">{error}</p>
                <p className="text-sm text-gray-600 mt-2">
                  This is usually temporary. Please wait a few seconds and try again.
                </p>
              </div>

              <div className="text-center">
                <button
                  onClick={generateOptimizedResume}
                  disabled={loading}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-col gap-3 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-blue-700">
                    Selected template: {TEMPLATE_CONFIG[selectedTemplate].label}
                  </p>
                  <p className="text-xs text-gray-600">
                    ATS-friendly layout designed for clean recruiter readability
                  </p>
                </div>

                <button
                  onClick={generateOptimizedResume}
                  disabled={loading}
                  className="bg-white text-blue-700 border border-blue-200 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-100 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Regenerate Resume
                </button>
              </div>

              <div className="max-w-full overflow-hidden rounded-xl"><pre className="a4-sheet m-0 max-h-[70vh] max-w-full overflow-y-auto whitespace-pre-wrap break-words rounded-xl border border-slate-200 bg-white p-4 font-sans text-sm leading-6 text-slate-800 shadow-inner [overflow-wrap:anywhere] sm:p-8">
                {previewResume}
              </pre></div>
            </div>
          )}
        </div>

        <div className="flex flex-col justify-center gap-3 text-center sm:flex-row">
          <button
            onClick={downloadResume}
            disabled={loading || !optimizedResume}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700 disabled:opacity-50 sm:w-auto"
          >
            <Download aria-hidden="true" className="h-4 w-4" />
            Download PDF
          </button>

          <button
            onClick={goCoverLetter}
            disabled={loading || !optimizedResume}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-6 py-3 font-semibold text-emerald-800 transition hover:bg-emerald-100 disabled:opacity-50 sm:w-auto"
          >
            Generate Cover Letter
          </button>

          <p className="mt-4 text-sm text-gray-500 italic sm:col-span-2">
            🔒 <span className="font-medium">Note:</span> Please review and personalize your downloaded resume. Replace any bracketed suggestions and double-check your contact details before applying.
          </p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default GenerateResume;
